import { InputBase, styled } from "@mui/material";
import { ConditionValueType as ConditionValueTypeEnum } from "@sinch/core/entities/serverEnums";
import type { ConditionCategory, ConditionType, ConditionValueType } from "@sinch/types/sinch.types";
import { isNotNil } from "ramda-adjunct";
import React from "react";
import {
  ConditionSelectProps,
  ConditionValue,
} from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/ConditionInput/ConditionInput";
import { ConditionalValueInputSelect } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/ConditionInput/ConditionValue/ConditionalValueInputSelect";
import { ConditionValueInputDate } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/ConditionInput/ConditionValue/ConditionValueInputDate";
import { ConditionValueInputNationalHoliday } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/ConditionInput/ConditionValue/ConditionValueInputNationalHoliday";
import { ConditionValueInputNumber } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/ConditionInput/ConditionValue/ConditionValueInputNumber";
import { ConditionValueInputString } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/ConditionInput/ConditionValue/ConditionValueInputString";
import { ConditionValueInputWeekdayTimes } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/ConditionInput/ConditionValue/ConditionValueInputWeekdayTimes";
import { NationalHolidayConditionFormFields } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/ConditionInput/ConditionValue/nationalHolidayFormSchema";
import { TimeIntervalTypeFormFields } from "@sinch/components/application/TimeIntervalSettings/TimeIntervalSettings";

export type ConditionValueInputProps = ConditionSelectProps<ConditionValue> & {
  type: ConditionValueType | ConditionValueTypeEnum | null;
  multiple?: boolean;
  category: ConditionCategory | null;
  subjectId: string | null;
  subjectType: ConditionType | null;
};

type ChangeFn<TVal> = (val: TVal | null) => void;

export const ConditionValueInput = ({
  onChange,
  onOpenChange,
  value,
  type,
  multiple,
  subjectId,
  category,
  onBlur,
  subjectType,
  readOnly,
}: ConditionValueInputProps) => {
  if (type === ConditionValueTypeEnum.WeekdayTimes) {
    return (
      <ConditionValueInputWeekdayTimes
        onBlur={onBlur}
        onChange={onChange as ChangeFn<TimeIntervalTypeFormFields>}
        onOpenChange={onOpenChange}
        readOnly={readOnly}
        value={value as TimeIntervalTypeFormFields}
      />
    );
  }

  if (type === ConditionValueTypeEnum.NationalHolidayTimes) {
    return (
      <ConditionValueInputNationalHoliday
        onBlur={onBlur}
        onChange={onChange as ChangeFn<NationalHolidayConditionFormFields>}
        onOpenChange={onOpenChange}
        readOnly={readOnly}
        value={value as NationalHolidayConditionFormFields}
      />
    );
  }

  if (
    isNotNil(type) &&
    isNotNil(subjectType) &&
    isNotNil(category) &&
    (multiple || type === ConditionValueTypeEnum.Select)
  ) {
    return (
      <ConditionalValueInputSelect
        category={category}
        multiple={multiple}
        onBlur={onBlur}
        onChange={onChange}
        onOpenChange={onOpenChange}
        readOnly={readOnly}
        subjectId={subjectId}
        subjectType={subjectType}
        value={value}
      />
    );
  }

  if (type === ConditionValueTypeEnum.Number) {
    return (
      <ConditionValueInputNumber
        onBlur={onBlur}
        onChange={onChange as ChangeFn<string>}
        onOpenChange={onOpenChange}
        readOnly={readOnly}
        value={value as string}
      />
    );
  }

  if (type === ConditionValueTypeEnum.Date) {
    return (
      <ConditionValueInputDate
        onBlur={onBlur}
        onChange={onChange as ChangeFn<Date>}
        onOpenChange={onOpenChange}
        readOnly={readOnly}
        value={value as Date}
      />
    );
  }

  if (type === ConditionValueTypeEnum.String) {
    return (
      <ConditionValueInputString
        onBlur={onBlur}
        onChange={onChange as ChangeFn<string>}
        onOpenChange={onOpenChange}
        readOnly={readOnly}
        value={value as string}
      />
    );
  }

  return (
    <DisabledInputBase
      disabled
      fullWidth
      inputProps={{
        "data-cy": "ConditionInput-valueInput-none",
      }}
    />
  );
};

const DisabledInputBase = styled(InputBase)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  backgroundColor: theme.palette.grey[200],
}));
